import { StatusMessage, Text } from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import type { BulkImportProblem, BulkImportRejection } from './bulkImportErrors'

export interface BulkImportProblemListProps {
  rejection: BulkImportRejection
  /**
   * Heading above the list. Defaults to the first-upload wording; the corrected
   * re-upload in Story 13 (KPS-602) passes its own, since that file was not the
   * admin's first attempt.
   */
  title?: string
}

/**
 * Everything wrong with a refused upload, reported at once.
 *
 * Problems are shown in two groups because they call for different things. A
 * whole-file problem — not permitted, account not ready, federated, too many rows,
 * unreadable, too large, or too few seats — cannot be fixed by editing a line, and
 * listing it among the row problems buries the only entries the admin can act on. Row
 * problems keep **the backend's own line number and the backend's own order**: the
 * number exists so the admin can find the row in the file they prepared, which
 * renumbering or re-sorting would defeat.
 *
 * Nothing is created by a rejected upload, and that is stated rather than left to be
 * inferred — an admin who is unsure whether a partial import happened has no safe next
 * move.
 */
export const BulkImportProblemList = ({
  rejection,
  title,
}: BulkImportProblemListProps): React.ReactElement => {
  const { t } = useTranslation()

  const fileProblems = rejection.problems.filter(
    problem => problem.source_line_number === null,
  )
  const rowProblems = rejection.problems.filter(
    problem => problem.source_line_number !== null,
  )

  const heading =
    title ??
    (rejection.seatLimitOnly
      ? t('BULK_IMPORT_REFUSED_SEATS')
      : t('BULK_IMPORT_FILE_REJECTED'))

  return (
    <div data-testid='bulk-import-problems'>
      <StatusMessage
        theme='danger'
        show
        title={heading}
        content={rejection.message}
      />
      <Text
        tag='p'
        margin={['med', 'none']}
        dataTestingId='bulk-import-nothing-created'
      >
        {t('BULK_IMPORT_NOTHING_CREATED')}
      </Text>

      {fileProblems.length > 0 && (
        <section aria-label={t('BULK_IMPORT_FILE_PROBLEMS')}>
          <Text tag='p' textWeight='bold' margin={['med', 'none', 'xs']}>
            {t('BULK_IMPORT_FILE_PROBLEMS')}
          </Text>
          <ul data-testid='bulk-import-file-problems'>
            {fileProblems.map((problem, index) => (
              <li key={`file-${index}`}>
                <Text tag='span'>{problem.message}</Text>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rowProblems.length > 0 && (
        <section aria-label={t('BULK_IMPORT_ROW_PROBLEMS')}>
          <Text tag='p' textWeight='bold' margin={['med', 'none', 'xs']}>
            {t('BULK_IMPORT_ROW_PROBLEMS')}
          </Text>
          <ul data-testid='bulk-import-row-problems'>
            {rowProblems.map((problem, index) => (
              <li key={`row-${index}`} data-testid='bulk-import-row-problem'>
                <RowProblem problem={problem} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

const RowProblem = ({
  problem,
}: {
  problem: BulkImportProblem
}): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <>
      {/* The number is rendered beside its label rather than interpolated into it, so
          the digits stay readable as digits — findable by an admin scanning the list,
          and assertable by a test that the backend's number reached the screen
          unchanged. */}
      <Text
        tag='span'
        textWeight='bold'
        dataTestingId='bulk-import-line-number'
      >
        {`${t('BULK_IMPORT_LINE')} ${problem.source_line_number}`}
      </Text>{' '}
      {problem.column && (
        <>
          <Text tag='span' dataTestingId='bulk-import-column'>
            {problem.column}
          </Text>{' '}
        </>
      )}
      <Text tag='span'>{problem.message}</Text>
    </>
  )
}
